// app/frontend/pages/Admin/Ads/AdForm.tsx
import React, { useState } from 'react';
import { usePage, router } from '@inertiajs/react';
import { useForm } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import {
  Container,
  Title,
  Button,
  Group,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Switch,
  Stack,
  Paper,
  Text,
  Grid,
  Box,
  FileInput,
  Image,
  Card,
  Badge,
  Anchor,
  Alert,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconEye,
  IconUpload,
  IconAlertCircle,
  IconCalendar,
} from '@tabler/icons-react';
import { DateTimePicker } from '@mantine/dates';
import { showNotification } from '@mantine/notifications';

interface Ad {
  id?: number;
  title: string;
  description: string;
  url: string;
  banner_url?: string;
  active: boolean;
  start_date?: string;
  end_date?: string;
  priority: number;
  ad_type: 'card' | 'banner' | 'popup';
  display_frequency: number;
}

interface AdType {
  value: string;
  label: string;
}

interface AdFormProps extends PageProps {
  ad: Ad;
  form_action: 'create' | 'update';
  ad_types: AdType[];
}

export default function AdForm() {
  const { ad, form_action, ad_types } = usePage<AdFormProps>().props;
  const [previewImage, setPreviewImage] = useState<string | null>(ad.banner_url || null);
  const [showPreview, setShowPreview] = useState(false);

  const { data, setData, post, put, processing, errors, reset } = useForm({
    title: ad.title || '',
    description: ad.description || '',
    url: ad.url || '',
    banner: null as File | null,
    active: ad.active !== undefined ? ad.active : true,
    start_date: ad.start_date ? new Date(ad.start_date) : null,
    end_date: ad.end_date ? new Date(ad.end_date) : null,
    priority: ad.priority || 0,
    ad_type: ad.ad_type || 'card',
    display_frequency: ad.display_frequency || 5,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('ad[title]', data.title);
    formData.append('ad[description]', data.description);
    formData.append('ad[url]', data.url);
    formData.append('ad[active]', data.active.toString());
    formData.append('ad[priority]', data.priority.toString());
    formData.append('ad[ad_type]', data.ad_type);
    formData.append('ad[display_frequency]', data.display_frequency.toString());

    if (data.start_date) {
      formData.append('ad[start_date]', data.start_date.toISOString());
    }
    if (data.end_date) {
      formData.append('ad[end_date]', data.end_date.toISOString());
    }
    if (data.banner) {
      formData.append('ad[banner]', data.banner);
    }

    if (form_action === 'create') {
      router.post('/admin/ads', formData, {
        forceFormData: true,
        onSuccess: () => {
          showNotification({
            title: 'Success',
            message: 'Ad created successfully!',
            color: 'green',
          });
          router.visit('/admin/ads');
        },
        onError: (errors) => {
          showNotification({
            title: 'Error',
            message: 'Please check the form for errors',
            color: 'red',
          });
        },
      });
    } else {
      router.post(
        `/admin/ads/${ad.id}`,
        {
          ...Object.fromEntries(formData),
          _method: 'patch',
        },
        {
          forceFormData: true,
          onSuccess: () => {
            showNotification({
              title: 'Success',
              message: 'Ad updated successfully!',
              color: 'green',
            });
            router.visit('/admin/ads');
          },
          onError: (errors) => {
            showNotification({
              title: 'Error',
              message: 'Please check the form for errors',
              color: 'red',
            });
          },
        },
      );
    }
  };

  const handleFileChange = (file: File | null) => {
    setData('banner', file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(ad.banner_url || null);
    }
  };

  const getAdTypeDescription = (type: string) => {
    switch (type) {
      case 'card':
        return 'Card ads appear interspersed with events in the grid layout. They match the size and style of event cards.';
      case 'banner':
        return 'Banner ads appear as horizontal banners above the main content area. Best for wide promotional images.';
      case 'popup':
        return 'Popup ads appear as modal overlays after a short delay. Use sparingly to avoid user annoyance.';
      default:
        return '';
    }
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <Container size="sm" py="xl">
      <form onSubmit={handleSubmit}>
        <Stack gap="xl">
          {/* Header */}
          <Stack gap="md">
            <Group>
              <Button
                variant="light"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => router.visit('/admin/ads')}
              >
                Back to Ads
              </Button>
            </Group>

            <Group justify="space-between">
              <div>
                <Title order={1} className="rubik text-white">
                  {form_action === 'create' ? 'Create New Ad' : 'Edit Ad'}
                </Title>
                <Text size="sm" c="dimmed">
                  {form_action === 'create'
                    ? 'Create a new advertisement for your platform'
                    : 'Update advertisement details and settings'}
                </Text>
              </div>

              <Group>
                <Button
                  variant="light"
                  leftSection={<IconEye size={16} />}
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
                <Button type="submit" loading={processing} leftSection={<IconDeviceFloppy size={16} />} color="#EA580C">
                  {form_action === 'create' ? 'Create Ad' : 'Update Ad'}
                </Button>
              </Group>
            </Group>
          </Stack>

          {/* Form Content */}
          <Grid>
            <Grid.Col span={{ base: 12, md: showPreview ? 8 : 12 }}>
              <Stack gap="md">
                {/* Basic Information */}
                <Paper p="md" withBorder style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                  <Stack gap="md">
                    <Text fw={600} size="lg" c="white">
                      Basic Information
                    </Text>

                    <TextInput
                      label="Ad Title"
                      placeholder="Enter a compelling headline for your ad"
                      value={data.title}
                      onChange={(e) => setData('title', e.currentTarget.value)}
                      error={errors.title}
                      required
                      maxLength={255}
                    />

                    <Textarea
                      label="Description"
                      placeholder="Enter a description that will encourage clicks"
                      value={data.description}
                      onChange={(e) => setData('description', e.currentTarget.value)}
                      error={errors.description}
                      minRows={3}
                      maxRows={5}
                    />

                    <TextInput
                      label="Target URL"
                      placeholder="https://example.com/landing-page"
                      value={data.url}
                      onChange={(e) => setData('url', e.currentTarget.value)}
                      error={errors.url || (!validateUrl(data.url) && data.url ? 'Please enter a valid URL' : '')}
                      required
                    />

                    {data.url && validateUrl(data.url) && (
                      <Text size="xs" c="dimmed">
                        Preview:{' '}
                        <Anchor href={data.url} target="_blank">
                          {data.url}
                        </Anchor>
                      </Text>
                    )}
                  </Stack>
                </Paper>

                {/* Ad Type & Settings */}
                <Paper p="md" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                  <Stack gap="md">
                    <Text fw={600} size="lg" c="white">
                      Ad Type & Settings
                    </Text>

                    <Select
                      label="Ad Type"
                      value={data.ad_type}
                      onChange={(value) => setData('ad_type', value as any)}
                      data={ad_types}
                      error={errors.ad_type}
                      required
                    />

                    {data.ad_type && (
                      <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                        {getAdTypeDescription(data.ad_type)}
                      </Alert>
                    )}

                    <Grid>
                      <Grid.Col span={6}>
                        <NumberInput
                          label="Display Frequency"
                          description="For card ads: show every N events. For others: not applicable"
                          value={data.display_frequency}
                          onChange={(value) => setData('display_frequency', Number(value))}
                          error={errors.display_frequency}
                          min={1}
                          max={20}
                          disabled={data.ad_type !== 'card'}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <NumberInput
                          label="Priority"
                          description="Higher numbers appear first (0 = lowest priority)"
                          value={data.priority}
                          onChange={(value) => setData('priority', Number(value))}
                          error={errors.priority}
                          min={0}
                          max={100}
                        />
                      </Grid.Col>
                    </Grid>

                    <Switch
                      label="Active"
                      description="Whether this ad should be displayed to users"
                      checked={data.active}
                      onChange={(event) => setData('active', event.currentTarget.checked)}
                    />
                  </Stack>
                </Paper>

                {/* Scheduling */}
                <Paper p="md" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                  <Stack gap="md">
                    <Text fw={600} size="lg" c="white">
                      Scheduling (Optional)
                    </Text>
                    <Text size="sm" c="dimmed">
                      Leave dates empty to run the ad indefinitely
                    </Text>

                    <Grid>
                      <Grid.Col span={6}>
                        <DateTimePicker
                          label="Start Date"
                          placeholder="When should this ad start showing?"
                          value={data.start_date}
                          onChange={(value) => setData('start_date', value)}
                          error={errors.start_date}
                          leftSection={<IconCalendar size={16} />}
                          clearable
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <DateTimePicker
                          label="End Date"
                          placeholder="When should this ad stop showing?"
                          value={data.end_date}
                          onChange={(value) => setData('end_date', value)}
                          error={errors.end_date}
                          leftSection={<IconCalendar size={16} />}
                          clearable
                          minDate={data.start_date || undefined}
                        />
                      </Grid.Col>
                    </Grid>

                    {data.start_date && data.end_date && data.start_date >= data.end_date && (
                      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                        End date must be after start date
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                {/* Image Upload */}
                <Paper p="md" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                  <Stack gap="md">
                    <Text fw={600} size="lg" c="white">
                      Ad Image
                    </Text>

                    <FileInput
                      label="Banner Image"
                      placeholder="Choose an image file"
                      accept="image/*"
                      onChange={handleFileChange}
                      error={errors.banner}
                      leftSection={<IconUpload size={16} />}
                      description="Recommended: 1200x200px for banners, 600x400px for cards. Max file size: 5MB"
                    />

                    {previewImage && (
                      <Box>
                        <Text size="sm" fw={500} mb="xs">
                          Current Image:
                        </Text>
                        <Image
                          src={previewImage}
                          alt="Ad preview"
                          style={{ maxHeight: 200, width: 'auto' }}
                          radius="md"
                        />
                      </Box>
                    )}

                    {data.ad_type === 'banner' && (
                      <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="filled">
                        Banner ads work best with wide images (6:1 ratio). Consider 1200x200px for optimal display.
                      </Alert>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>

            {/* Preview Panel */}
            {showPreview && (
              <Grid.Col span={4}>
                <Paper p="md" style={{ position: 'sticky', top: 20 }}>
                  <Stack gap="md">
                    <Group>
                      <Text fw={600} size="lg" c="white">
                        Live Preview
                      </Text>
                      <Badge color={data.ad_type === 'card' ? 'blue' : data.ad_type === 'banner' ? 'orange' : 'purple'}>
                        {data.ad_type}
                      </Badge>
                    </Group>

                    {/* Card Ad Preview */}
                    {data.ad_type === 'card' && (
                      <Card shadow="sm" padding="lg" radius="md" withBorder style={{ maxWidth: 300 }}>
                        {previewImage && (
                          <Card.Section>
                            <Image src={previewImage} height={160} alt={data.title} />
                          </Card.Section>
                        )}
                        <Group justify="space-between" mt="md" mb="xs">
                          <Text fw={500} size="sm" lineClamp={1}>
                            {data.title || 'Ad Title'}
                          </Text>
                          <Badge color="orange" size="sm">
                            Sponsored
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {data.description || 'Ad description will appear here...'}
                        </Text>
                        <Button color="#EA580C" fullWidth mt="md" radius="md" size="xs">
                          Learn More
                        </Button>
                      </Card>
                    )}

                    {/* Banner Ad Preview */}
                    {data.ad_type === 'banner' && (
                      <Box>
                        <Card
                          padding="lg"
                          radius="md"
                          withBorder
                          style={{
                            height: 120,
                            backgroundImage: previewImage
                              ? `url(${previewImage})`
                              : 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'relative',
                            color: 'white',
                          }}
                        >
                          <Box
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(0, 0, 0, 0.4)',
                              borderRadius: '8px',
                            }}
                          />
                          <Box style={{ position: 'relative', zIndex: 1 }}>
                            <Badge color="orange" size="sm" mb="xs">
                              Sponsored
                            </Badge>
                            <Text fw={700} size="md" lineClamp={1}>
                              {data.title || 'Banner Ad Title'}
                            </Text>
                            <Text size="sm" lineClamp={2} opacity={0.9}>
                              {data.description || 'Banner ad description...'}
                            </Text>
                          </Box>
                        </Card>
                      </Box>
                    )}

                    {/* Popup Ad Preview */}
                    {data.ad_type === 'popup' && (
                      <Card shadow="xl" padding="lg" radius="md" withBorder style={{ maxWidth: 350 }}>
                        <Stack gap="md">
                          <Group justify="space-between">
                            <Badge color="purple" size="sm">
                              Sponsored
                            </Badge>
                            <Text size="xs" c="dimmed">
                              ✕
                            </Text>
                          </Group>
                          {previewImage && <Image src={previewImage} height={120} alt={data.title} radius="md" />}
                          <Stack gap="xs">
                            <Text fw={600} size="lg" lineClamp={1}>
                              {data.title || 'Popup Ad Title'}
                            </Text>
                            <Text size="sm" c="dimmed" lineClamp={3}>
                              {data.description || 'Popup ad description will appear here...'}
                            </Text>
                          </Stack>
                          <Button color="#EA580C" fullWidth radius="md">
                            Learn More
                          </Button>
                        </Stack>
                      </Card>
                    )}

                    <Text size="xs" c="dimmed">
                      This is a preview of how your ad will appear to users.
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            )}
          </Grid>

          {/* Error Summary */}
          {Object.keys(errors).length > 0 && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              <Text fw={500}>Please fix the following errors:</Text>
              <ul style={{ margin: '0.5rem 0 0 1rem' }}>
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>
                    <Text size="sm">{error}</Text>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Form Actions */}
          <Group justify="space-between">
            <Button variant="light" onClick={() => router.visit('/admin/ads')} disabled={processing}>
              Cancel
            </Button>
            <Group>
              {form_action === 'update' && (
                <Button variant="light" color="gray" onClick={() => reset()} disabled={processing}>
                  Reset Changes
                </Button>
              )}
              <Button type="submit" loading={processing} leftSection={<IconDeviceFloppy size={16} />} color="#EA580C">
                {form_action === 'create' ? 'Create Ad' : 'Update Ad'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}
